import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { postDataAPI, getDataAPI } from '../../../../../utils/fetchData';
import { setErrorAlert, setSuccessAlert } from '../../../../../store/alert/alertSlice';
import DizeeInput from '../../../../../components/DizeeComponent/DizeeInput';
import LayoutHeader from '../../../../../layout/LayoutHeader';
import Plus from '../../../../../svg/Plus';
import Check from '../../../../../svg/Check';
import { canManageTeam, isDelegatedUser } from '../../../../../utils/permissions';
import styled from 'styled-components';

const Container = styled.div`
  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: white;
    margin: 0 0 15px 0;
  }

  .invite-section {
    margin-bottom: 30px;
  }

  .manager-notice {
    margin-bottom: 30px;
  }

  .section-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
    margin: 30px 0;
  }

  .team-member-card {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
    padding: 0;
  }

  .member-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(45deg, #6366f1, #8b5cf6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 16px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .member-info {
    flex: 1;
    min-width: 0;
  }

  .member-name {
    color: white;
    font-size: 13px;
    font-weight: 400;
    margin-bottom: 2px;
  }

  .member-email {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    margin-bottom: 2px;
  }

  .member-role {
    color: rgba(255, 255, 255, 0.8);
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    margin-top: 2px;
  }

  .action-button {
    background: transparent;
    border: none;
    color: white;
    padding: 8px 12px;
    border-radius: 0;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s ease;
  }

  .action-button:hover {
    background: transparent;
    opacity: 0.7;
  }

  .action-button.danger {
    border: none;
    color: #ff6363;
  }

  .action-button.danger:hover {
    background: transparent;
    opacity: 0.7;
  }

  .permission-note {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 10px;
    line-height: 1.4;
  }
`;

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('manager'); // Add role state
  const [dropdownOpen, setDropdownOpen] = useState(false); // Add dropdown state
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [confirmDeleteEmail, setConfirmDeleteEmail] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userArticle } = useSelector((state) => state.user);

  // Role options with descriptions (only Manager and Support)
  const roleOptions = [
    {
      value: 'manager',
      label: 'Manager',
      description: 'Full-featured team member with customizable permissions. Manage the artist\'s career, handle business affairs, and coordinate with other team members.'
    },
    {
      value: 'support',
      label: 'Support',
      description: 'View-only access to selected apps. Perfect for assistants, interns, or team members who need limited access to help with specific tasks.'
    }
  ];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setDropdownOpen(false);
  };

  const selectedRoleOption = roleOptions.find(role => role.value === selectedRole);

  // Check user context - distinguish between actual artist and team member in delegated mode
  const selectedArtist = localStorage.getItem('selectedArtist');
  const localStorageUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
  
  // True artist: professionRole is 'artist' AND not in delegated mode
  const isActualArtist = userArticle?.professionRole === 'artist' && !selectedArtist;

  // Team member in delegated mode: accessing artist account but should have view-only access
  const isTeamMemberDelegated = selectedArtist && ['manager', 'support'].includes(localStorageUser?.professionRole);
  
  // For UI purposes: show team management interface if either actual artist OR team member in delegated mode
  const showTeamManagement = isActualArtist || isTeamMemberDelegated;
  
  // Check user permissions
  const canManageTeamPermission = canManageTeam(); // Use utility function

  console.log('🎭 Team Management Access Debug:');
  console.log('- selectedArtist:', selectedArtist);
  console.log('- userArticle.professionRole:', userArticle?.professionRole);
  console.log('- localStorageUser.professionRole:', localStorageUser?.professionRole);
  console.log('- isActualArtist:', isActualArtist);
  console.log('- isTeamMemberDelegated:', isTeamMemberDelegated);
  console.log('- showTeamManagement:', showTeamManagement);
  console.log('- canManageTeam:', canManageTeamPermission);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.role-dropdown')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchTeamMembers = async () => {
    setLoadingMembers(true);
    try {
      const token = localStorage.getItem('dizeeToken');
      console.log('🔍 Token for team members:', !!token);
      const response = await postDataAPI('team/members', {
        userId: userArticle?._id,
        userRole: userArticle?.professionRole
      }, token);

      if (response.data?.status === 200) {
        setTeamMembers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      dispatch(setErrorAlert('Failed to load team members'));
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) {
      dispatch(setErrorAlert('Please enter an email address'));
      return;
    }

    if (!isValidEmail(email)) {
      dispatch(setErrorAlert('Please enter a valid email address'));
      return;
    }

    setLoadingInvite(true);
    
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setLoadingInvite(false);
      dispatch(setErrorAlert('Request timed out. Please try again.'));
    }, 30000); // 30 second timeout

    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/invite', {
        email: email.trim(),
        role: selectedRole,
        accountType: selectedRole, // New account type system
        inviterRole: userArticle?.professionRole,
        inviterId: userArticle?._id,
        artistId: isActualArtist ? userArticle?._id : userArticle?.managedArtist?._id
      }, token);

      clearTimeout(timeoutId);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Team invitation sent successfully!'));
        setEmail('');
        setSelectedRole('manager'); // Reset to default role
        fetchTeamMembers(); // Refresh the list
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to send invitation'));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error sending invitation:', error);
      dispatch(setErrorAlert('Failed to send invitation'));
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleRemoveMember = async (memberEmail) => {
    if (!isActualArtist) {
      dispatch(setErrorAlert('Only artists can remove team members'));
      return;
    }

    setLoadingRemove(true);
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/remove-member', {
        memberEmail: memberEmail,
        artistId: userArticle?._id
      }, token);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Team member removed successfully'));
        setConfirmDeleteEmail('');
        fetchTeamMembers(); // Refresh the list
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to remove team member'));
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      dispatch(setErrorAlert('Failed to remove team member'));
    } finally {
      setLoadingRemove(false);
    }
  };

  const handleCancelInvitation = async (invitedEmail) => {
    if (!isActualArtist) {
      dispatch(setErrorAlert('Only artists can cancel invitations'));
      return;
    }

    setLoadingRemove(true);
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/cancel-invitation', {
        invitedEmail: invitedEmail,
        artistId: userArticle?._id
      }, token);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Invitation cancelled successfully'));
        setConfirmDeleteEmail('');
        fetchTeamMembers(); // Refresh the list
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to cancel invitation'));
      }
    } catch (error) {
      console.error('Error canceling invitation:', error);
      dispatch(setErrorAlert('Failed to cancel invitation'));
    } finally {
      setLoadingRemove(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleDisplayName = (role, status) => {
    if (status === 'pending') return 'Pending';
    
    switch (role) {
      case 'artist': return 'Artist (Owner)';
      case 'manager': return 'Manager';
      case 'support': return 'Support';
      default: return 'Team Member';
    }
  };

  return (
    <LayoutHeader>
      <Container>
        <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
          <div className="px-4 my-[50px] flex w-full justify-between">
            <p className="text-white">Team Management</p>
            <div className='flex gap-[20px]'>
              <p onClick={() => navigate('/more/setting')} className='text-white cursor-pointer'>Go back</p>
            </div>
          </div>

          <div className="w-full px-4">
            {/* Invite Section - Only for Actual Artists */}
            {isActualArtist && (
              <div className="invite-section">
                <div className="text-white text-[12px] font-semibold">
                  Invite Team Members
                </div>
                <div className="flex flex-col gap-y-[20px]">
                  <div className='flex w-full items-center gap-x-[43px]'>
                    <DizeeInput
                      placeholder="Enter team member email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                    />
                    <div className="flex gap-2 items-center">
                    <button 
                        className="action-button primary"
                      onClick={handleInvite}
                      disabled={loadingInvite}
                    >
                      {loadingInvite ? 'Inviting...' : 'Invite'}
                    </button>
                    </div>
                  </div>
                  
                  {/* Role Selection - Styled Dropdown */}
                  <div className="flex flex-col gap-y-[10px]">
                    <div className="text-white text-[12px] font-semibold">Select Role:</div>
                    <div className="relative role-dropdown">
                      <div 
                        className="bg-black rounded-[4px] pr-4 py-3 cursor-pointer flex justify-between items-center text-white text-[12px] transition-all duration-200"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                      >
                        <span>{selectedRoleOption ? selectedRoleOption.label : "Select role..."}</span>
                        <div 
                          className="w-0 h-0 transition-transform duration-200"
                          style={{
                            borderLeft: '4px solid transparent',
                            borderRight: '4px solid transparent',
                            borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                            transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </div>
                      
                      {dropdownOpen && (
                        <div
                          className="absolute top-full left-0 right-0 bg-[#111111] rounded-[4px] mt-1 z-50 overflow-hidden shadow-lg max-h-[300px] overflow-y-auto"
                        >
                          {/* Small spacer at top */}
                          <div className="h-2 bg-[#111111]"></div>
                          
                          <div className="px-[5px] flex flex-col gap-y-[10px]">
                            {roleOptions.map((role) => (
                              <div
                                key={role.value}
                                className={`flex flex-col gap-y-1 cursor-pointer p-3 rounded transition-all duration-200 border ${
                                  selectedRole === role.value 
                                    ? 'border-[#FF2300]' 
                                    : 'border-transparent hover:border-[#FF2300]'
                                }`}
                                onClick={() => handleRoleSelect(role.value)}
                              >
                                <div className="flex items-center gap-2 text-white text-[12px] font-semibold">
                                  {role.label}
                                  {selectedRole === role.value && (
                                    <Check className="h-[12px] w-[18px]" />
                                  )}
                                </div>
                                <div className="text-white/70 text-[10px] leading-[1.3]">
                                  {role.description}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Small spacer at bottom */}
                          <div className="h-2 bg-[#111111]"></div>
                        </div>
                    )}
                    </div>
                  </div>
                  
                  <div className="permission-note">
                    The selected role will determine what access this team member has to your account.
                  </div>
                </div>
              </div>
            )}

            {/* View-Only Notice for Team Members in Delegated Mode */}
            {isTeamMemberDelegated && (
              <div className="manager-notice">
                <div className="text-white text-[12px] font-semibold">
                  Team Member View: You can view the team members but only the artist can add or remove team members.
                </div>
              </div>
            )}

            {!showTeamManagement && (
              <div className="manager-notice">
                <div className="text-white text-[12px] font-semibold">
                  Manager View: You can view team members but only the artist can add or remove team members.
                </div>
              </div>
            )}

            <div className="section-divider"></div>

            {/* Active Team Members Section */}
            {(() => {
              const activeMembers = teamMembers.filter(member => member.status === 'active');
              const pendingInvitations = teamMembers.filter(member => member.status === 'pending');

              return (
                <>
                  <div className="text-white text-[12px] font-semibold">
                    Active Team Members ({activeMembers.length})
            </div>

            {loadingMembers ? (
              <div className="text-white text-center py-8">Loading team members...</div>
                  ) : activeMembers.length > 0 ? (
                    <div style={{ marginTop: '20px' }}>
                      {activeMembers.map((member, index) => (
                  <div key={index} className="team-member-card">
                    <div className="member-avatar">
                      {member.profilePicture ? (
                        <img 
                          src={member.profilePicture} 
                          alt={member.name}
                          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        getInitials(member.name || member.email)
                      )}
                    </div>
                    
                    <div className="member-info">
                      <div className="member-name">
                              {member.name || member.email || 'Unnamed User'}
                      </div>
                            {/* Only show email if it's different from the name */}
                            {member.name && member.name !== member.email && (
                      <div className="member-email">
                        {member.email}
                      </div>
                            )}
                      <div className="member-role">
                        {getRoleDisplayName(member.role, member.status)}
                      </div>
                    </div>

                    <div className="member-actions">
                            {isActualArtist && member.role !== 'artist' && (
                            <>
                              <button
                                onClick={() => navigate(`/more/setting/advanced/team-management/edit/${member._id}`)}
                                className="action-button"
                                style={{ marginRight: '8px' }}
                              >
                                Edit
                              </button>
                              {confirmDeleteEmail === member.email ? (
                                <button 
                                  onClick={() => handleRemoveMember(member.email)}
                                  className="action-button danger"
                                  disabled={loadingRemove}
                                >
                                  {loadingRemove ? 'Removing...' : 'Confirm Remove'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => setConfirmDeleteEmail(member.email)}
                                  className="action-button danger"
                                >
                                  Remove
                                </button>
                              )}
                            </>
                          )}
                          
                            {!isActualArtist && member.role === 'artist' && (
                            <span className="action-button" style={{ opacity: 0.6 }}>
                              Owner
                            </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
                    <div className="text-white text-center py-4">
                      <p>No active team members yet.</p>
                    </div>
                  )}

                  {/* Pending Invitations Section */}
                  {pendingInvitations.length > 0 && (
                    <>
                      <div className="section-divider" style={{ margin: '30px 0 20px 0' }}></div>
                      <div className="text-white text-[12px] font-semibold">
                        Pending Invitations ({pendingInvitations.length})
                      </div>
                      <div style={{ marginTop: '20px' }}>
                        {pendingInvitations.map((invitation, index) => (
                          <div key={index} className="team-member-card" style={{ opacity: 0.7 }}>
                            <div className="member-info">
                              <div className="member-name">
                                {invitation.email}
                              </div>
                              <div className="member-role">
                                {getRoleDisplayName(invitation.role, 'pending')} • Invited {new Date(invitation.invitedAt).toLocaleDateString()}
                              </div>
                              {invitation.expiresAt && (
                                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px' }}>
                                  Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                                </div>
                              )}
                            </div>

                            <div className="member-actions">
                              {isActualArtist ? (
                                confirmDeleteEmail === invitation.email ? (
                                  <button 
                                    onClick={() => handleCancelInvitation(invitation.email)}
                                    className="action-button danger"
                                    disabled={loadingRemove}
                                  >
                                    {loadingRemove ? 'Cancelling...' : 'Confirm Cancel'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDeleteEmail(invitation.email)}
                                    className="action-button danger"
                                  >
                                    Cancel
                                  </button>
                                )
                              ) : (
                                <span className="action-button" style={{ opacity: 0.6 }}>
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="permission-note" style={{ marginTop: '15px' }}>
                        Pending invitations will automatically move to "Active Team Members" when the invited person accepts and completes their signup.
                      </div>
                    </>
                  )}
                </>
              );
            })()}

            {!loadingMembers && teamMembers.length === 0 && (
              <div className="text-white text-center py-8">
                <p>No team members yet.</p>
                {canManageTeamPermission && (
                  <p className="text-sm opacity-60 mt-2">
                    Invite managers and collaborators to help grow your presence on Dizee.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </LayoutHeader>
  );
};

export default TeamManagement;